/* eslint-disable */
import {
  noActionTypesApiError,
  noBodyApiError,
  noMethodApiError,
  noServerApiError,
  noUrlApiError,
  sendFailAction,
  sendStartAction,
  sendSuccessAction,
} from './apiSupport';

import { DELETE, GET, PATCH, POST, PUT, REST_API } from '@shared/constants/rest-api';
import apiService from '@shared/services/api-service';

import _isFunction from 'lodash/isFunction';

const allowedMethods = [GET, POST, PATCH, PUT, DELETE];
const requiresBody = [PUT, POST];

// Global AbortController management
class AbortControllerManager {
  constructor() {
    this.controllers = new Map();
    this.disabledControllers = new Set(); // Track controllers that should not be auto-aborted
    this.pendingRequests = new Map(); // Track pending requests for cleanup
  }

  async createController(actionId, disableAutoAbort = false) {
    // Cancel existing controller for the same action if it exists
    if (this.controllers.has(actionId)) {
      const existingController = this.controllers.get(actionId);
      existingController.abort('Request superseded by new request');

      // Принудительно очищаем все связанные ресурсы
      this.forceCleanup(actionId);

      // Даем браузеру время обработать отмену
      await new Promise(resolve => setTimeout(resolve, 5));
    }

    const controller = new AbortController();
    this.controllers.set(actionId, controller);
    this.pendingRequests.set(actionId, Date.now());

    // Track if this controller should be immune to global abort
    if (disableAutoAbort) {
      this.disabledControllers.add(actionId);
    }

    // Автоматически удаляем контроллер через максимальное время
    setTimeout(() => {
      if (this.controllers.has(actionId)) {
        this.forceCleanup(actionId);
      }
    }, 600000); // 10 мин максимум для любого запроса

    return controller;
  }

  forceCleanup(actionId) {
    if (this.controllers.has(actionId)) {
      try {
        this.controllers.get(actionId).abort('Force cleanup');
      } catch (e) {
        // Игнорируем ошибки при принудительной очистке
      }
    }
    this.controllers.delete(actionId);
    this.disabledControllers.delete(actionId);
    this.pendingRequests.delete(actionId);
  }

  trackRequest(actionId) {
    this.pendingRequests.set(actionId, Date.now());
  }

  removeController(actionId) {
    this.controllers.delete(actionId);
    this.disabledControllers.delete(actionId);
    this.pendingRequests.delete(actionId);
  }

  abortController(actionId) {
    this.forceCleanup(actionId);
  }

  abortAll() {
    this.controllers.forEach((controller, actionId) => {
      // Only abort controllers that don't have disableAutoAbort
      if (!this.disabledControllers.has(actionId)) {
        try {
          controller.abort('Abort all requests');
        } catch (e) {
          // Игнорируем ошибки при массовой отмене
        }
      }
    });

    // Clean up aborted controllers (keep only disabled ones)
    const disabledControllersToKeep = new Map();
    const disabledPendingToKeep = new Map();

    this.disabledControllers.forEach(actionId => {
      if (this.controllers.has(actionId)) {
        disabledControllersToKeep.set(actionId, this.controllers.get(actionId));
        disabledPendingToKeep.set(actionId, this.pendingRequests.get(actionId));
      }
    });

    this.controllers.clear();
    this.pendingRequests.clear();
    this.controllers = disabledControllersToKeep;
    this.pendingRequests = disabledPendingToKeep;

    // Update disabled set to only include kept controllers
    const newDisabledSet = new Set();
    disabledControllersToKeep.forEach((_, actionId) => {
      newDisabledSet.add(actionId);
    });
    this.disabledControllers = newDisabledSet;
  }

  getActiveCount() {
    return this.controllers.size;
  }

  // Новый метод для принудительной очистки зависших запросов
  cleanupStaleRequests(maxAge = 30000) {
    const now = Date.now();
    const staleIds = [];

    this.pendingRequests.forEach((timestamp, actionId) => {
      if (now - timestamp > maxAge) {
        staleIds.push(actionId);
      }
    });

    staleIds.forEach(actionId => this.forceCleanup(actionId));
    return staleIds.length;
  }
}

// Create global instance
const abortManager = new AbortControllerManager();

// Фильтрация query-параметров
function cleanQueryParams(params = {}) {
  const result = {};

  for (const key in params) {
    const value = params[key];
    const isEmpty =
      value === undefined ||
      value === null ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0) ||
      (Object.prototype.toString.call(value) === '[object Object]' && Object.keys(value).length === 0);

    if (!isEmpty) {
      result[key] = value;
    }
  }

  return result;
}

// Generate unique action ID for abort controller management
function generateActionId(action) {
  // Use action-specific ID if provided
  if (action.abortId) {
    return action.abortId;
  }

  // Generate ID based on URL and method only (same endpoint = same ID)
  // Remove query parameters from URL to ensure same base endpoint gets same ID
  const baseUrl = action.url?.split('?')[0] || 'unknown';
  const urlPart = baseUrl.replace(/[^a-zA-Z0-9]/g, '_');
  const methodPart = action.method || 'get';

  return `${methodPart}_${urlPart}`;
}

const RestApiMiddleware = store => next => async action => {
  /** *************** */
  if (action.skip) {
    return next(action); // todo DELETE AFTER STORE REFACTORING
  }
  /** ************** */

  if (action.type !== REST_API) {
    return next(action);
  }

  if (!~allowedMethods.indexOf(action.method)) {
    return next(noMethodApiError(action));
  }

  if (typeof action.url !== 'string' || action.url.length === 0) {
    return next(noUrlApiError(action));
  }

  if (
    typeof action.body !== 'object' &&
    ~requiresBody.indexOf(action.method) &&
    action.headers['Content-Type'] === 'application/json'
  ) {
    return next(noBodyApiError(action));
  }

  if (!Array.isArray(action.types) || action.types.length !== 3) {
    return next(noActionTypesApiError(action));
  }

  // Generate action ID for abort controller management
  const actionId = generateActionId(action);
  let controllerCreated = false; // Track if we created a controller
  let requestRemoved = false; // Предотвращаем двойную очистку

  const safeRemoveController = () => {
    if (controllerCreated && !requestRemoved) {
      requestRemoved = true;
      abortManager.removeController(actionId);
    }
  };

  try {
    store.dispatch(sendStartAction(action));

    if (action.headers.Authorization === undefined) {
      action.headers.Authorization = '';
    }

    // Create AbortController for tracking and cancellation
    const controller = await abortManager.createController(actionId, action.disableAutoAbort === true);
    controllerCreated = true; // We always create a controller for tracking

    // Determine which signal to use based on disableAutoAbort flag
    let signal;
    if (action.disableAutoAbort === true || action.signal) {
      // If disableAutoAbort is explicitly true AND external signal provided, use external signal
      signal = action.signal || undefined;
    } else {
      // Otherwise, always use our internal controller signal for proper abort management
      signal = controller.signal;
    }

    // Очищаем query-параметры от пустых значений
    const cleanedParams = cleanQueryParams(action?.params);

    // Проверяем, не был ли запрос уже отменен перед отправкой
    if (signal?.aborted) {
      safeRemoveController();
      return Promise.resolve();
    }

    const { data, headers } = await apiService.request({
      baseURL: '',
      url: action.url,
      method: action.method,
      headers: action.headers,
      data: action.body,
      responseType: action.responseType || 'json',
      fullResponse: true,
      withoutRefresh: !!action.withoutRefresh,
      signal,
      params: cleanedParams,
      timeout: action.timeout || 120000, // Добавляем timeout по умолчанию 120000 млсек
    });

    // Проверяем, не был ли запрос отменен во время выполнения
    if (signal?.aborted) {
      safeRemoveController();
      return Promise.resolve();
    }

    // Remove controller from manager after successful completion if we created it
    safeRemoveController();

    const responseData = typeof data === 'string' ? { data } : data;
    const responseHeaders = {
      get(name) {
        return headers[String(name).toLowerCase()];
      },
    };

    store.dispatch(sendSuccessAction(action, responseData, responseHeaders));

    _isFunction(action.onSuccess) && action.onSuccess(responseData, responseHeaders);
  } catch (error) {
    // Remove controller from manager on error ONLY if we created it and haven't removed it yet
    safeRemoveController();

    // Проверяем, является ли это отменённым запросом (CanceledError)
    if (
      error.name === 'CanceledError' ||
      error.code === 'ERR_CANCELED' ||
      error.message?.includes('canceled') ||
      error.message?.includes('aborted') ||
      error.message?.includes('Request superseded')
    ) {
      return Promise.resolve(); // Немедленно завершаем без дальнейшей обработки
    }

    if (!error.response) {
      throw error;
    }

    const {
      response: { status, statusText, data },
    } = error;
    const message = (data && data.message) || '';
    _isFunction(action.onError) && action.onError({ statusText, status, message });
    store.dispatch(sendFailAction(action, message));
    throw next(noServerApiError(status));
  }
};

// Export the middleware and utilities
export default RestApiMiddleware;

// Export abort manager for external control
export { abortManager };

// Helper functions for external use
export const abortRequest = actionId => {
  abortManager.abortController(actionId);
};

export const abortAllRequests = () => {
  abortManager.abortAll();
};

export const getActiveRequestsCount = () => {
  return abortManager.getActiveCount();
};

export const cleanupStaleRequests = (maxAge = 30000) => {
  return abortManager.cleanupStaleRequests(maxAge);
};
